import type { DataGridProProps, GridApi, GridColDef } from '@mui/x-data-grid-pro';
import type { TableColumnDTO } from '@tempo-io/roadmap-dto';
import { parseTableColumnField } from './tableColumnUtils';

export type OnColumnOrderChange = (columns: TableColumnDTO[]) => void;

export const useOnColumnOrderChange = (
  columns: GridColDef[],
  layoutColumns: TableColumnDTO[],
  onColumnOrderChange?: OnColumnOrderChange,
) => {
  if (!onColumnOrderChange) return undefined;

  const onItemsTableColumnOrderChange: DataGridProProps['onColumnOrderChange'] = (
    { column, targetIndex },
    _event,
    { api },
  ) => {
    if (!layoutColumns) return;

    const { type, customFieldId } = parseTableColumnField(column.field);
    const actualOldIndex = layoutColumns.findIndex(
      (col) => col.type === type && col.customFieldId === customFieldId,
    );
    if (actualOldIndex === -1) return;

    const gridApi = api as unknown as GridApi;
    const pinned = gridApi.getPinnedColumns();
    const existingFields = new Set(gridApi.getAllColumns().map((col) => col.field));
    const leftPinned = (pinned.left ?? []).filter((field) => existingFields.has(field));
    const leftOffset = leftPinned.length;
    const adjustedIndex = targetIndex - leftOffset;

    if (adjustedIndex < -1) return;
    const clampedIndex = Math.max(adjustedIndex, 0);

    const leftPinnedFields = new Set(leftPinned);
    const rightPinnedFields = new Set(pinned.right ?? []);
    const unpinnedColumns = columns.filter(
      (col) => !leftPinnedFields.has(col.field) && !rightPinnedFields.has(col.field),
    );

    const actualTargetCol = unpinnedColumns[clampedIndex];
    if (!actualTargetCol) return;

    const { type: targetType, customFieldId: targetCustomFieldId } = parseTableColumnField(
      actualTargetCol.field,
    );
    const actualTargetIndex = layoutColumns.findIndex(
      (col) => col.type === targetType && col.customFieldId === targetCustomFieldId,
    );
    if (actualTargetIndex === -1) return;

    const reorderedColumns = [...layoutColumns];
    const reorderedColumn = reorderedColumns.splice(actualOldIndex, 1)[0];
    if (reorderedColumn) {
      reorderedColumns.splice(actualTargetIndex, 0, reorderedColumn);
      onColumnOrderChange(reorderedColumns);
    }
  };

  return onItemsTableColumnOrderChange;
};
